import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.jsx';

export default function Clients() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['clients', q], queryFn: () => api(`/api/clients?q=${encodeURIComponent(q)}`) });
  const [editing, setEditing] = useState(null);

  const save = useMutation({
    mutationFn: p => p.id
      ? api(`/api/clients/${p.id}`, { method: 'PATCH', body: p.data })
      : api('/api/clients', { method: 'POST', body: p.data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clients'] }); setEditing(null); },
  });

  return (
    <>
      <div className="page-header">
        <h1>{t('clients.title')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <input placeholder={t('common.search')} value={q} onChange={e => setQ(e.target.value)} />
          <button className="btn btn-primary" onClick={() => setEditing({})}>{t('clients.add')}</button>
        </div>
      </div>
      {isLoading ? <div className="card">{t('common.loading')}</div> : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('common.name')}</th><th>{t('clients.type')}</th><th>{t('clients.nuis')}</th>
              <th>{t('common.email')}</th><th>{t('common.phone')}</th>
              <th>Docs</th><th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.clients?.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong>{c.legalName ? <div className="muted" style={{ fontSize: 12 }}>{c.legalName}</div> : null}</td>
                <td>{c.type === 'COMPANY' ? t('clients.company') : t('clients.individual')}</td>
                <td>{c.nuis || '—'}</td>
                <td>{c.email || '—'}</td>
                <td>{c.phone || '—'}</td>
                <td>{c._count?.documents ?? 0}</td>
                <td><button className="btn btn-secondary btn-sm" onClick={() => setEditing(c)}>{t('common.edit')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <ClientModal client={editing} onClose={() => setEditing(null)} onSave={d => save.mutate({ id: editing.id, data: d })} saving={save.isPending} error={save.error?.data} />
      )}
    </>
  );
}

function ClientModal({ client, onClose, onSave, saving, error }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    type: client.type || 'COMPANY',
    name: client.name || '',
    legalName: client.legalName || '',
    nuis: client.nuis || '',
    vatNumber: client.vatNumber || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    city: client.city || '',
    country: client.country || 'XK',
    defaultLang: client.defaultLang || 'en',
    paymentTermsDays: client.paymentTermsDays ?? 15,
    notes: client.notes || '',
    contacts: client.contacts || [],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateContact = (i, k, v) => setForm(f => {
    const contacts = [...f.contacts]; contacts[i] = { ...contacts[i], [k]: v }; return { ...f, contacts };
  });
  const addContact = () => setForm(f => ({ ...f, contacts: [...f.contacts, { name: '', email: '', phone: '', role: '', isPrimary: f.contacts.length === 0 }] }));
  const removeContact = i => setForm(f => ({ ...f, contacts: f.contacts.filter((_, j) => j !== i) }));

  return (
    <Modal title={client.id ? t('common.edit') : t('clients.add')} onClose={onClose} wide>
      <form onSubmit={e => { e.preventDefault(); onSave(form); }}>
        {error && <div className="error-banner">{error.error}</div>}
        <div className="row">
          <div className="field"><label>{t('clients.type')}</label>
            <select value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="COMPANY">{t('clients.company')}</option>
              <option value="INDIVIDUAL">{t('clients.individual')}</option>
            </select>
          </div>
          <div className="field"><label>{t('common.name')}</label><input required value={form.name} onChange={e => set('name', e.target.value)} /></div>
        </div>
        {form.type === 'COMPANY' && (
          <div className="row">
            <div className="field"><label>{t('clients.legalName')}</label><input value={form.legalName} onChange={e => set('legalName', e.target.value)} /></div>
            <div className="field"><label>{t('clients.nuis')}</label><input value={form.nuis} onChange={e => set('nuis', e.target.value)} /></div>
            <div className="field"><label>{t('clients.vatNumber')}</label><input value={form.vatNumber} onChange={e => set('vatNumber', e.target.value)} /></div>
          </div>
        )}
        <div className="row">
          <div className="field"><label>{t('common.email')}</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div className="field"><label>{t('common.phone')}</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
        </div>
        <div className="field"><label>{t('clients.address')}</label><input value={form.address} onChange={e => set('address', e.target.value)} /></div>
        <div className="row">
          <div className="field"><label>{t('clients.city')}</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>
          <div className="field"><label>{t('clients.country')}</label><input maxLength={2} value={form.country} onChange={e => set('country', e.target.value.toUpperCase())} /></div>
          <div className="field"><label>{t('clients.defaultLang')}</label>
            <select value={form.defaultLang} onChange={e => set('defaultLang', e.target.value)}>
              <option value="en">EN</option><option value="sq">SQ</option>
            </select>
          </div>
          <div className="field"><label>{t('clients.paymentTerms')}</label><input type="number" min={0} value={form.paymentTermsDays} onChange={e => set('paymentTermsDays', Number(e.target.value))} /></div>
        </div>
        <div className="field"><label>{t('clients.notes')}</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} /></div>

        <h3 className="mt-16">{t('clients.contacts')}</h3>
        {form.contacts.map((c, i) => (
          <div key={i} className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field"><label>{t('common.name')}</label><input value={c.name} onChange={e => updateContact(i, 'name', e.target.value)} /></div>
            <div className="field"><label>{t('common.email')}</label><input type="email" value={c.email || ''} onChange={e => updateContact(i, 'email', e.target.value)} /></div>
            <div className="field"><label>{t('common.phone')}</label><input value={c.phone || ''} onChange={e => updateContact(i, 'phone', e.target.value)} /></div>
            <div className="field"><label>Role</label><input value={c.role || ''} onChange={e => updateContact(i, 'role', e.target.value)} /></div>
            <div className="field"><label><input type="checkbox" checked={!!c.isPrimary} onChange={e => updateContact(i, 'isPrimary', e.target.checked)} /> {t('clients.primary')}</label></div>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => removeContact(i)}>×</button>
          </div>
        ))}
        <button type="button" className="btn btn-secondary btn-sm" onClick={addContact}>{t('clients.addContact')}</button>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}
